import { Template, BareTemplate } from './template';
import { BareEvent } from './event';
import { VenueDisposition } from './plan';
import { BareSailing, BareSailingDay } from './sailing';
import { FeedPage } from './feedTemplate';
import { BareContent } from './content';

export type TemplateFragmentUnlinkStrategy = 'partial' | 'full';
export type TemplateFragmentType = 'events' | 'venueData' | 'feed' | 'content';

export const FRAGMENT_TYPES: TemplateFragmentType[] = ['events', 'venueData', 'feed', 'content'];

export interface BasicTemplateFragment {
  templateId: string;
  fragmentType: TemplateFragmentType;
}

export interface ResolvedTemplateFragment {
  template: Template;
  fragmentType: TemplateFragmentType;
}

export interface BasicTemplateDayAssociation {
  templateId: string;
  sailingId: string;
  sailingDayId: string;
}

export interface ResolvedTemplateDayAssociation {
  template: BareTemplate;
  sailing: BareSailing;
  sailingDay: BareSailingDay;
  feedSlotNumber?: number;
}

export type ResolvedDayAssociation = Omit<ResolvedTemplateDayAssociation, 'template'>;
export type ResolvedTemplateAssociation = Omit<ResolvedTemplateDayAssociation, 'sailingDay'>;

export interface FragmentDelta<T> {
  kind: 'create' | 'update' | 'delete';
  change: T;
}

export interface EventFragmentDelta {
  fragmentType: 'events';
  deltas: Array<FragmentDelta<BareEvent>>;
}

export interface VenueDataFragmentDelta {
  fragmentType: 'venueData';
  deltas: Array<FragmentDelta<VenueDisposition>>;
}

export interface FeedFragmentDelta {
  fragmentType: 'feed';
  deltas: Array<FragmentDelta<FeedPage>>;
}

export interface ContentFragmentDelta {
  fragmentType: 'content';
  deltas: Array<FragmentDelta<BareContent>>;
}

export type WithFragmentDelta<T> = T &
  (EventFragmentDelta | VenueDataFragmentDelta | FeedFragmentDelta | ContentFragmentDelta);

export type WithStrategy<T> = T & {
  unlinkStrategy: TemplateFragmentUnlinkStrategy;
};

export type WithFragment<T> = T & {
  fragmentType: TemplateFragmentType;
};

// these are just shortcuts for commonly used types in client world
// since client likes to bundle a template day association with a particular
// fragment, and sometimes with an unlink strategy. these are known as
// link fragments and strategic link fragments
export type LinkFragment = WithFragment<BasicTemplateDayAssociation>;
export type StrategicLinkFragment = WithStrategy<LinkFragment>;
